import { useId } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styled from '@emotion/styled';

interface AreaTrendChartProps {
  data: { date: string; value: number }[];
  formatValue?: (value: number) => string;
  color?: string;
  height?: number;
}

// "2026-10-31" → "Oct 31"; anything else (hours, labels) is shown as is.
const shortDate = (raw: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(`${raw}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const compact = (v: number) =>
  Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);

// The overview's headline chart (Stripe/Mercury): one soft area, horizontal
// hairlines only, few ticks, a crosshair tooltip.
const AreaTrendChart: React.FC<AreaTrendChartProps> = ({
  data,
  formatValue = (v) => v.toLocaleString('en-US'),
  color = '#1F7A45',
  height = 260,
}) => {
  const gradientId = useId().replace(/:/g, '');

  return (
    <ResponsiveContainer width='100%' height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={color} stopOpacity={0.18} />
            <stop offset='100%' stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke='#EFECE4' />
        <XAxis
          dataKey='date'
          tickFormatter={shortDate}
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          minTickGap={32}
          tick={{ fontSize: 12, fill: '#7A8279' }}
        />
        <YAxis
          width='auto'
          tickFormatter={compact}
          tickLine={false}
          axisLine={false}
          tickCount={4}
          tick={{ fontSize: 12, fill: '#7A8279' }}
        />
        <Tooltip
          cursor={{ stroke: '#A9A395', strokeDasharray: '3 3' }}
          content={({ active, payload, label }) =>
            active && payload?.length ? (
              <Tip>
                <span>{shortDate(String(label))}</span>
                <strong>{formatValue(Number(payload[0].value))}</strong>
              </Tip>
            ) : null
          }
        />
        <Area
          type='monotone'
          dataKey='value'
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff', fill: color }}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default AreaTrendChart;

const Tip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #0e1a13;
  color: #ffffff;
  font-size: 12px;
  box-shadow: 0 8px 24px -8px rgba(14, 26, 19, 0.4);

  & span {
    color: #b4beb5;
  }
  & strong {
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }
`;
